import { batch } from 'react-redux'
import { startFetch, successfulFetch, hideModalPageSpinner } from '../ui/ui-slice'
import { recordApiRequest, recordApiResponse, recordClientEvent } from '@/shared/utils/fpti'
import {
  initEnrollCard,
  initEnrollCardPreEligibilityReducer,
  updateCard,
  setXoPPDCContent,
} from './enroll-card-slice'
import { setToastMessage } from '../set-pin/set-pin-slice'
import { setToastMessage as setFundingInstrumentToastMessage } from '../funding-instruments/funding-instruments-slice'
import {
  initEnrollment,
  applyForEnrollment,
  enrollCard as enrollCardService,
  enrollCardGB as enrollCardGBService,
  enrollCardXO as enrollCardXOService,
  reissueCard,
  initEnrollmentPreEligibility,
  fetchXoPPDCContent,
  enrollLoyaltyMember,
} from '../../services/card'
import {
  checkRUGSResponse,
  checkRUGSResponseGB,
  checkRUGSResponsePPDCXO,
  shouldRedirectToCardManagement,
  shouldRedirectToCfpbPage,
  shouldRedirectToCipPage,
  isShippingAddressRequired,
  hasPayrollPartnerIntent,
  getFieldsToIncludeByFlow,
  checkKYCandRedirect,
  filterRecommendations,
  handleReissueCardError,
} from 'utils/screens/enroll-card'
import { formatContentMessage } from 'utils/content'
import { handleFailedFetch, handleUnexpectedError } from 'utils/error/error-handler'
import { goToSetPinFlow } from 'utils/screens/activate-card'
import {
  goToCardManagementFlow,
  goToChangeAddressFlow,
  returnToOrigin,
  goToReplaceCardFlow,
} from 'utils/navigate'
import { isDigitalOnlyCard, shouldSetPin } from 'utils/card'
import { FLOWS } from 'universal/ucs'
import { APPLICATION_STATUS, SUB_OPERATIONS } from 'universal/cfs-card'
import { ADDRESS_TYPES } from 'universal/address'
import { INTENT_TYPES } from 'universal/constants'
import { FLOW_STEPS } from '@/constants/flow-steps'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import {
  ENROLLMENT_TOUCHPOINTS,
  ENROLLMENT_TOUCHPOINTS_GB,
} from '@/constants/personalization-studio'
import ENROLL_CARD_FPTI_TAGS, { EnrollCardApiCalls } from '@/constants/fpti-tags/enroll-card'
import XO_PPDC_FPTI_TAGS from '@/constants/fpti-tags/xo-ppdc'
import { hasFeature } from 'utils/server-data'
import {
  setToastMessageInSessionStorage,
  removeToastMessagesFromSessionStorage,
} from 'utils/storage'
import { UCS_TOAST_MESSAGE_EVENT_IDS } from '@/constants/common'
import { PPDCXO_CONSTANTS } from '@/constants/ppdcxo'
const { PPDC_UPSELL_TOUCHPOINT, PPDCXO_STEPUP_ENTRYPOINT, NO_UNIT_ID, STEPUP_REQUIRED } =
  PPDCXO_CONSTANTS

const { ENROLL_CARD } = FLOWS
const { ELIGIBLE_STEP } = FLOW_STEPS.ENROLL_CARD
const { PPDC_UPSELL_XO } = FLOW_STEPS.XO_PPDC
const { APPROVED, STEPUP_AUTH } = APPLICATION_STATUS
const { SHIPPING } = ADDRESS_TYPES
const { cardEnrolledSuccessActivityTags } = ENROLL_CARD_FPTI_TAGS
const {
  enrollmentSuccessActivityTags,
  enrollmentApiErrorActivityTags,
  eligibilityInvalidActivityTags,
  stepupRedirectActivityTags,
  enrollmentLeapStartActivityTags,
  enrollmentLeapSuccessActivityTags,
  enrollmentLeapFailActivityTags,
  applicationInvalidActivityTags,
  mismatchingOfferIdTags,
  incentiveScreenImpressionTags,
  upsellScreenImpressionTags,
} = XO_PPDC_FPTI_TAGS
const { CONVERT_FORM_FACTOR } = SUB_OPERATIONS
export function initCardEligibility({ productName = '', addressId, intent, returnUri, cancelUri }) {
  return async function _initCardEligibility(dispatch, getState, fetchRetried, options = {}) {
    const fetchName = initCardEligibility.name
    const flowName = ENROLL_CARD

    const touchpointDetails = [...ENROLLMENT_TOUCHPOINTS[ELIGIBLE_STEP]]

    const recommendationContext = {
      touchpoint_details: touchpointDetails.map((code) => ({
        code,
        message_count: 10,
      })),
    }

    try {
      dispatch(startFetch({ fetchName, flowName }))
      const includeFields: any = getFieldsToIncludeByFlow()

      // Record API request for PS Studio data
      recordApiRequest(EnrollCardApiCalls.INIT_ENROLLMENT, {
        product_name: productName,
        has_address_id: !!addressId,
        intent,
      })

      const { cardEligibilities, recommendations, applyCard, ...includedFieldsResponse } =
        await initEnrollment(
          {
            productName,
            addressId,
            recommendationContext,
            includeFields: Object.values(includeFields).join(','),
            intent,
          },
          options,
        )

      // Record API response success
      recordApiResponse(
        EnrollCardApiCalls.INIT_ENROLLMENT,
        {
          product_name: productName,
          has_address_id: !!addressId,
          intent,
        },
        true,
        {
          has_recommendations: !!recommendations,
          recommendations_count: recommendations ? Object.keys(recommendations).length : 0,
          card_eligibility_status: cardEligibilities?.[productName]?.status,
        },
      )

      const { [productName]: cardEligibility, ...otherCardEligibilities } = cardEligibilities || {}

      if (shouldRedirectToCardManagement(cardEligibilities)) {
        const cardDetails = {
          productName: applyCard?.cardAttributes?.productName,
        }

        if (hasPayrollPartnerIntent()) {
          const [existingDebitCard] = includedFieldsResponse[includeFields.userCards]

          if (isDigitalOnlyCard(existingDebitCard)) {
            const { cardId } = existingDebitCard
            const params = {
              productName,
              reason: REPLACE_CARD_REASONS.GET_PHYSICAL_CARD,
              intent,
              returnUri,
              cancelUri, // If user decides not to continue, we should take them to payroll-integration route
              skipSuccess: true,
            }

            goToReplaceCardFlow(cardId, params)
            return
          }

          ;(returnToOrigin as any)()
          return
        }

        goToCardManagementFlow(cardDetails, { rs: 'manageCard' })
        return
      }

      if (shouldRedirectToCipPage(applyCard, dispatch)) {
        return
      }

      batch(() => {
        dispatch(
          initEnrollCard({ cardEligibility, otherCardEligibilities, recommendations, applyCard }),
        )
        dispatch(successfulFetch({ fetchName, flowName }))
      })
    } catch (err) {
      // Record API response error
      recordApiResponse(
        EnrollCardApiCalls.INIT_ENROLLMENT,
        {
          product_name: productName,
          has_address_id: !!addressId,
          intent,
        },
        false,
        {
          error_message: err.message,
          error_type: err.name,
        },
      )

      handleFailedFetch(
        err,
        {
          fetchName,
          dispatch,
          getState,
          retryAction: _initCardEligibility,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function initCardEligibilityWithPreEligibility({
  productName = '',
  addressId,
  intent,
  returnUri,
  cancelUri,
}) {
  return async function _initCardEligibilityWithPreEligibility(
    dispatch,
    getState,
    fetchRetried,
    options = {},
  ) {
    const fetchName = initCardEligibilityWithPreEligibility.name
    const flowName = ENROLL_CARD

    const touchpointDetails = [...ENROLLMENT_TOUCHPOINTS_GB[ELIGIBLE_STEP]]

    const recommendationContext = {
      touchpoint_details: touchpointDetails.map((code) => ({
        code,
        message_count: -1,
      })),
    }

    try {
      dispatch(startFetch({ fetchName, flowName }))
      const includeFields: any = getFieldsToIncludeByFlow()

      // Record API request for PS Studio pre-eligibility data
      recordApiRequest(EnrollCardApiCalls.INIT_ENROLLMENT_PRE_ELIGIBILITY, {
        product_name: productName,
        has_address_id: !!addressId,
        intent,
      })

      const { cardEligibilities, loyaltyStatus, recommendations, ...includedFieldsResponse } =
        await initEnrollmentPreEligibility(
          {
            productName,
            addressId,
            recommendationContext,
            includeFields: Object.values(includeFields).join(','),
            intent,
          },
          options,
        )

      // Record API response success
      recordApiResponse(
        EnrollCardApiCalls.INIT_ENROLLMENT_PRE_ELIGIBILITY,
        {
          product_name: productName,
          has_address_id: !!addressId,
          intent,
        },
        true,
        {
          has_recommendations: !!recommendations,
          recommendations_count: recommendations ? Object.keys(recommendations).length : 0,
          loyalty_status: loyaltyStatus,
          card_eligibility_status: cardEligibilities?.[productName]?.status,
        },
      )

      const { [productName]: cardEligibility } = cardEligibilities || {}

      if (shouldRedirectToCardManagement(cardEligibilities)) {
        const cardDetails = {
          productName: cardEligibility?.cardAttributes?.productName,
        }

        if (hasPayrollPartnerIntent()) {
          const [existingDebitCard] = includedFieldsResponse[includeFields.userCards]

          if (isDigitalOnlyCard(existingDebitCard)) {
            const { cardId } = existingDebitCard
            const params = {
              productName,
              reason: REPLACE_CARD_REASONS.GET_PHYSICAL_CARD,
              intent,
              returnUri,
              cancelUri, // If user decides not to continue, we should take them to payroll-integration route
              skipSuccess: true,
            }

            goToReplaceCardFlow(cardId, params)
            return
          }

          ;(returnToOrigin as any)()
          return
        }

        goToCardManagementFlow(cardDetails, { rs: 'manageCard' })
        return
      }

      // if (shouldRedirectToCipPage(applyCard, dispatch)) {
      //   return
      // }

      batch(() => {
        dispatch(
          initEnrollCardPreEligibilityReducer({ cardEligibility, loyaltyStatus, recommendations }),
        )
        dispatch(successfulFetch({ fetchName, flowName }))
      })
    } catch (err) {
      // Record API response error
      recordApiResponse(
        EnrollCardApiCalls.INIT_ENROLLMENT_PRE_ELIGIBILITY,
        {
          product_name: productName,
          has_address_id: !!addressId,
          intent,
        },
        false,
        {
          error_message: err.message,
          error_type: err.name,
        },
      )

      handleFailedFetch(
        err,
        {
          fetchName,
          dispatch,
          getState,
          retryAction: _initCardEligibilityWithPreEligibility,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function enrollLoyaltyMemberAction({ worldReady }, options) {
  return async function _enrollLoyaltyMember(dispatch, getState, fetchRetried) {
    const fetchName = enrollLoyaltyMemberAction.name
    const flowName = ENROLL_CARD
    try {
      dispatch(startFetch({ fetchName, flowName }))
      const { loyaltyEnrollSuccess } = await enrollLoyaltyMember(options)

      const formattedMessage = formatContentMessage(
        loyaltyEnrollSuccess
          ? 'enrollCard.toast.loyalty.enrollment.success'
          : 'enrollCard.toast.loyalty.enrollment.success',
        worldReady,
      )
      batch(() => {
        dispatch(
          setFundingInstrumentToastMessage({
            toastMessage: {
              message: formattedMessage,
              isSuccess: loyaltyEnrollSuccess,
            },
          }),
        )
        dispatch(successfulFetch({ fetchName, flowName }))
      })
    } catch (err) {
      handleFailedFetch(
        err,
        {
          fetchName,
          dispatch,
          getState,
          retryAction: _enrollLoyaltyMember,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function applyForEnrollCard({ nextStep, productName }) {
  return async function _applyForEnrollCard(dispatch, getState, fetchRetried) {
    const fetchName = applyForEnrollCard.name
    const flowName = ENROLL_CARD
    try {
      let applyCard = getState().enrollCard?.cardApplication
      const hasStatusOrErrorStatus = applyCard?.status || applyCard?.errorStatus

      if (!hasStatusOrErrorStatus) {
        dispatch(startFetch({ fetchName, flowName }))
        const applyForEnrollmentResponse = await (applyForEnrollment as any)(productName)
        applyCard = applyForEnrollmentResponse.applyCard
        dispatch(successfulFetch({ fetchName, sysData: applyForEnrollmentResponse.sysData }))
      }

      if (applyCard?.status === APPROVED || isShippingAddressRequired(applyCard)) {
        ;(goToChangeAddressFlow as any)({
          flowName,
          dispatch,
          productName,
          addressType: SHIPPING,
          ...(hasPayrollPartnerIntent() ? { variant: INTENT_TYPES.PAYROLL_PARTNER } : {}),
        })
        return
      }

      if (shouldRedirectToCfpbPage(applyCard, dispatch)) {
        return
      }

      if (shouldRedirectToCipPage(applyCard, dispatch)) {
        return
      }

      // Check for STEPUP_AUTH, DENIED, NEED_MORE_DATA and MANUAL_REVIEW
      if (checkRUGSResponse({ applyCard, nextStep, dispatch })) {
        return
      }
    } catch (err) {
      handleFailedFetch(
        err,
        {
          fetchName,
          nextStep,
          dispatch,
          getState,
          retryAction: _applyForEnrollCard,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function checkGBEnrollmentAbility({ nextStep, goToNextStep, selectedFundingInstrumentId }) {
  return function (dispatch, getState) {
    const cardEligibility = getState().enrollCard?.cardEligibility
    // To be called by Add Money flow after Value Prop Page

    checkKYCandRedirect(
      cardEligibility,
      dispatch,
      nextStep,
      goToNextStep,
      selectedFundingInstrumentId,
    )
  }
}

export function enrollCard({
  productName,
  addressId,
  nextStep,
  worldReady,
  skipSuccess,
  logActivity,
}) {
  return async function _enrollCard(dispatch, getState, fetchRetried) {
    const fetchName = enrollCard.name
    const flowName = ENROLL_CARD
    try {
      if (fetchRetried) {
        // Display loading page when user click on Try Again
        dispatch(startFetch({ fetchName, flowName }))
      }

      const {
        applyCard = {},
        card,
        sysData,
      } = await (enrollCardService as any)({
        productName,
        addressId,
      })

      if (card?.cardId) {
        logActivity(cardEnrolledSuccessActivityTags)
        batch(() => {
          dispatch(updateCard({ card }))
          dispatch(successfulFetch({ fetchName, sysData }))
        })

        const formattedMessage = formatContentMessage(
          'enrollCard.toast.enrollment.success',
          worldReady,
        )

        if (hasFeature('enableManageCard')) {
          setToastMessageInSessionStorage(
            UCS_TOAST_MESSAGE_EVENT_IDS.ENROLLMENT_SUCCESS,
            formattedMessage,
          )
        }

        if (skipSuccess) {
          ;(returnToOrigin as any)()
          return
        }

        if (shouldSetPin(card)) {
          // Removing the toast message, to prevent showing toast twice
          removeToastMessagesFromSessionStorage()

          dispatch(
            setToastMessage({
              toastMessage: formattedMessage,
            }),
          )
          goToSetPinFlow(card, { flowName: FLOWS.ENROLL_CARD })
          return
        }

        goToCardManagementFlow(card)
        return
      }

      // Check for STEPUP_AUTH, DENIED, NEED_MORE_DATA and MANUAL_REVIEW
      if (applyCard.status !== APPROVED) {
        if (checkRUGSResponse({ applyCard, nextStep, dispatch })) {
          return
        }
      }
    } catch (err) {
      handleFailedFetch(
        err,
        {
          fetchName,
          nextStep,
          dispatch,
          getState,
          retryAction: _enrollCard,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function enrollCardGB({
  selectedFundingInstrumentId,
  productName,
  nextStep,
  goToNextStep,
  worldReady,
  skipSuccess,
  logActivity,
}) {
  return async function _enrollCardGB(dispatch, getState, fetchRetried) {
    const fetchName = enrollCard.name
    const flowName = ENROLL_CARD
    try {
      if (fetchRetried) {
        // Display loading page when user click on Try Again
        dispatch(startFetch({ fetchName, flowName }))
      }

      const {
        applyCard = {},
        card,
        sysData,
      } = await (enrollCardGBService as any)({
        selectedFundingInstrumentId,
        productName,
      })

      if (card?.cardId) {
        logActivity(cardEnrolledSuccessActivityTags)
        batch(() => {
          dispatch(updateCard({ card }))
          dispatch(successfulFetch({ fetchName, sysData }))
        })

        const formattedMessage = formatContentMessage(
          'enrollCard.cardEnrollingPage.toast.enrollment.ready',
          worldReady,
        )

        if (hasFeature('enableManageCard')) {
          setToastMessageInSessionStorage(
            UCS_TOAST_MESSAGE_EVENT_IDS.ENROLLMENT_SUCCESS,
            formattedMessage,
          )
        }

        if (skipSuccess) {
          ;(returnToOrigin as any)()
          return
        }

        if (shouldSetPin(card)) {
          // Removing the toast message, to prevent showing toast twice
          removeToastMessagesFromSessionStorage()

          dispatch(
            setToastMessage({
              toastMessage: formattedMessage,
            }),
          )
          goToSetPinFlow(card, { flowName: FLOWS.ENROLL_CARD })
          return
        }

        goToCardManagementFlow(card)
        return
      }

      // Check for STEPUP_AUTH, DENIED, NEED_MORE_DATA and MANUAL_REVIEW
      if (applyCard.status !== APPROVED) {
        if (
          (checkRUGSResponseGB as any)({
            applyCard,
            nextStep,
            goToNextStep,
            dispatch,
            selectedFundingInstrumentId,
          })
        ) {
          return
        }
      }
    } catch (err) {
      handleFailedFetch(
        err,
        {
          fetchName,
          nextStep,
          dispatch,
          getState,
          retryAction: _enrollCardGB,
          fetchRetried,
        },
        handleUnexpectedError,
      )
    }
  }
}

export function reissueCardGB({
  cardId,
  addressId,
  nextStep,
  goToNextStep,
  worldReady,
  skipSuccess,
  logActivity,
}) {
  return async function _reissueCardGB(dispatch, getState, fetchRetried) {
    const fetchName = reissueCardGB.name

    try {
      if (!cardId) {
        throw new Error('Card ID is required for reissuing a card')
      }

      const result = await (reissueCard as any)(cardId, addressId, CONVERT_FORM_FACTOR)
      const { card, sysData } = result

      if (card?.cardId) {
        logActivity(cardEnrolledSuccessActivityTags)
        batch(() => {
          dispatch(updateCard({ card }))
          dispatch(successfulFetch({ fetchName, sysData }))
        })

        const formattedMessage = formatContentMessage(
          'enrollCard.toast.enrollment.success',
          worldReady,
        )

        if (hasFeature('enableManageCard')) {
          setToastMessageInSessionStorage(
            UCS_TOAST_MESSAGE_EVENT_IDS.ENROLLMENT_SUCCESS,
            formattedMessage,
          )
        }

        if (skipSuccess) {
          ;(returnToOrigin as any)()
          return
        }

        setTimeout(() => {
          nextStep()
        }, 3000)
        return
      }
    } catch (err) {
      handleFailedFetch(
        err,
        {
          fetchName,
          nextStep,
          goToNextStep,
          dispatch,
          getState,
          retryAction: _reissueCardGB,
          fetchRetried,
        },
        handleReissueCardError,
      )
    }
  }
}

export function initXoPPDCContent() {
  /* eslint-disable max-statements */
  /* eslint-disable complexity */
  return async function _initXoPPDCContent(dispatch, _getState, _fetchRetried) {
    const fetchName = initXoPPDCContent.name
    const flowName = ENROLL_CARD

    try {
      dispatch(startFetch({ fetchName, flowName, isFromMainPage: false }))

      const touchpointDetails = [...ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]]

      const recommendationContext = {
        touchpoint_details: touchpointDetails.map((code) => ({
          code,
          message_count: -1,
        })),
      }

      const response = await fetchXoPPDCContent(recommendationContext)
      let recommendations = filterRecommendations(response.recommendations || [])

      const shouldHideRecommendations = response.recommendations?.some((rec) =>
        rec.messages?.some((msg) => {
          const showHint = msg?.content?.json?.showHint
          return showHint && showHint.toLowerCase() === 'hide'
        }),
      )
      if (shouldHideRecommendations) {
        recommendations = []
      }

      const { xt, xe, instrumentationData } = extractInstrumentationData(response.recommendations)

      let offerUnitId = response.offerUnitId

      if (!offerUnitId && response.recommendations?.length > 0) {
        for (const rec of response.recommendations) {
          if (rec.offerUnitId) {
            offerUnitId = rec.offerUnitId
            break
          }
          if (rec.messages?.length > 0) {
            for (const msg of rec.messages) {
              if (msg.offerUnitId) {
                offerUnitId = msg.offerUnitId
                break
              }
              // Check in content.json
              const contentJson = msg?.content?.json
              if (contentJson?.offerUnitId) {
                offerUnitId = contentJson.offerUnitId
                break
              }
            }
            if (offerUnitId) break
          }
        }
      }

      const selectedOfferId =
        window.ppdcApp?.selectedOfferId || window.PAYPAL?.checkout?.properties?.selectedOfferId
      const bothOfferIdsMissing = selectedOfferId === NO_UNIT_ID && !offerUnitId
      const isOfferValid = bothOfferIdsMissing || selectedOfferId === offerUnitId

      if (recommendations && Object.keys(recommendations).length > 0) {
        // Recommendations are present. Few things to consider:
        // 1. This is an Impression event.
        // 2. There will be no incomplete SC case as recommendations are present
        // 3. If ModXO and MORS did not provide any Offer ID, then it is a regular '5%' upsell screen
        // 4. If ModXO and MORS provided an Offer ID and it is valid, then it is a '$10' incentive screen

        if (bothOfferIdsMissing) {
          // No offer unit ID is present; hence regular '5%' upsell screen
          recordClientEvent(upsellScreenImpressionTags.eventName, {
            ...upsellScreenImpressionTags,
            xt,
            xe,
            instrumentationData,
            touchpointId: [...ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]]?.[0] || null,
            entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
            url_path: PPDCXO_CONSTANTS.URL_PATH,
            productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
          })
        } else if (isOfferValid) {
          // Offer unit ID is present and valid; hence '$10' incentive screen
          recordClientEvent(incentiveScreenImpressionTags.eventName, {
            ...incentiveScreenImpressionTags,
            offerUnitId,
            xt,
            xe,
            instrumentationData,
            touchpointId: [...ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]]?.[0] || null,
            entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
            url_path: PPDCXO_CONSTANTS.URL_PATH,
            productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
          })
        } else {
          recordClientEvent(mismatchingOfferIdTags.eventName, {
            ...mismatchingOfferIdTags,
            xt,
            xe,
            instrumentationData,
            touchpointId: [...ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]]?.[0] || null,
            selectedOfferId,
            offerUnitId,
            entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
            url_path: PPDCXO_CONSTANTS.URL_PATH,
            productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
          })
        }
      }
      // Recommendations are not present

      batch(() => {
        dispatch(
          setXoPPDCContent({
            recommendations,
            offerUnitId,
            selectedOfferId,
            isOfferValid,
            xt,
            xe,
            instrumentationData,
          }),
        )
        dispatch(successfulFetch({ fetchName, flowName }))
      })
    } catch (err) {
      // Always show ErrorPage for this iframe flow - never show SessionExpired/RequestBlocked/Retry
      dispatch(hideModalPageSpinner())
      dispatch(
        setXoPPDCContent({
          recommendations: [],
          isOfferValid: false,
          apiError: {
            type: err.type,
            message: err.message,
            code: err.code,
          },
        }),
      )
      // Don't call handleFailedFetch - bypasses middleware completely
      // Note: Using 'apiError' instead of 'error' to avoid middleware interception
    }
  }
}

function trackOfferActivation(offerActivation, productName, offerUnitId, touchpointId) {
  if (!offerActivation?.attempted) {
    return
  }

  recordClientEvent(enrollmentLeapStartActivityTags.eventName, {
    ...enrollmentLeapStartActivityTags,
    productName,
    offer_unit_id: offerUnitId,
    touchpoint_id: touchpointId,
  })

  if (offerActivation.successful) {
    recordClientEvent(enrollmentLeapSuccessActivityTags.eventName, {
      ...enrollmentLeapSuccessActivityTags,
      productName,
      offer_unit_id: offerUnitId,
      touchpoint_id: touchpointId,
      retry_attempt: offerActivation.retryAttempt || 0,
    })
  } else {
    recordClientEvent(enrollmentLeapFailActivityTags.eventName, {
      ...enrollmentLeapFailActivityTags,
      productName,
      offer_unit_id: offerUnitId,
      touchpoint_id: touchpointId,
      retry_attempts: offerActivation.retryAttempts,
    })
  }
}

export function enrollPPDCXO({ productName }) {
  /* eslint-disable complexity */
  return async function _enrollPPDCXO(dispatch, getState, fetchRetried) {
    const fetchName = enrollPPDCXO.name
    const flowName = ENROLL_CARD

    try {
      if (fetchRetried) {
        dispatch(startFetch({ fetchName, flowName }))
      }

      let response

      const xoPPDCData = getState()?.enrollCard?.xoPPDCContent || {}
      const { offerUnitId } = xoPPDCData || {}
      const touchpointId = ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]?.[0] || PPDC_UPSELL_TOUCHPOINT

      response = await (enrollCardXOService as any)({
        productName,
        offerUnitId,
        touchpointId,
      })

      triggerClickUrl(response.recommendations)

      const { card, sysData, applyCard, offerActivation, eligibilityResult, errorDetails } =
        response

      // Check for eligibility business logic outcome
      if (eligibilityResult) {
        recordClientEvent(eligibilityInvalidActivityTags.eventName, {
          ...eligibilityInvalidActivityTags,
          productName,
          offerUnitId,
          touchpointId,
          eligibilityStatus: eligibilityResult.resultDetail?.eligibilityStatus,
          eligibilityReasons: JSON.stringify(eligibilityResult.resultDetail?.details || []),
          entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
          url_path: PPDCXO_CONSTANTS.URL_PATH,
        })
        return null
      }

      if (errorDetails) {
        recordClientEvent(enrollmentApiErrorActivityTags.eventName, {
          ...enrollmentApiErrorActivityTags,
          productName,
          offerUnitId,
          touchpointId,
          errorType: errorDetails.type || 'Unknown',
          errorMessage: errorDetails.message || 'Unknown error',
          errorCode: errorDetails.code || 'UNKNOWN',
          responseCorrId: errorDetails.response_corr_id || 'N/A',
          entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
          url_path: PPDCXO_CONSTANTS.URL_PATH,
        })
        return null
      }

      if (card?.cardId) {
        batch(() => {
          dispatch(updateCard({ card }))
          dispatch(successfulFetch({ fetchName, sysData }))
        })

        recordClientEvent(enrollmentSuccessActivityTags.eventName, {
          ...enrollmentSuccessActivityTags,
          cardId: card.cardId,
          productName,
          offerUnitId,
          touchpointId,
          entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
          url_path: PPDCXO_CONSTANTS.URL_PATH,
        })

        trackOfferActivation(offerActivation, productName, offerUnitId, touchpointId)

        return card
      }

      if (applyCard) {
        if (applyCard.errorStatus === STEPUP_AUTH) {
          recordClientEvent(stepupRedirectActivityTags.eventName, {
            ...stepupRedirectActivityTags,
            productName,
            offerUnitId,
            touchpointId,
            entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
            url_path: PPDCXO_CONSTANTS.URL_PATH,
          })

          if (
            checkRUGSResponsePPDCXO({
              applyCard,
              dispatch,
              options: { entrypoint: PPDCXO_STEPUP_ENTRYPOINT },
            })
          ) {
            return STEPUP_REQUIRED
          }
        } else {
          // Application business logic outcome (needs more data, etc.)
          recordClientEvent(applicationInvalidActivityTags.eventName, {
            ...applicationInvalidActivityTags,
            productName,
            offerUnitId,
            touchpointId,
            errorStatus: applyCard.errorStatus || applyCard.status,
            requiredValue: applyCard.requiredValue,
            entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
            url_path: PPDCXO_CONSTANTS.URL_PATH,
          })
        }
      }

      return null
    } catch (err) {
      // Keep existing FPTI tracking
      recordClientEvent(enrollmentApiErrorActivityTags.eventName, {
        ...enrollmentApiErrorActivityTags,
        productName,
        errorMessage: err.message || 'Unknown error',
        errorCode: err.code || err.status || 'UNKNOWN',
        errorDescription: err.type || 'Unknown',
        offerUnitId: getState()?.enrollCard?.xoPPDCContent?.offerUnitId,
        touchpointId: ENROLLMENT_TOUCHPOINTS[PPDC_UPSELL_XO]?.[0] || PPDC_UPSELL_TOUCHPOINT,
        entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
        url_path: PPDCXO_CONSTANTS.URL_PATH,
      })

      // Always show ErrorPage for this iframe flow
      dispatch(hideModalPageSpinner())
      const xoPPDCContent = getState()?.enrollCard?.xoPPDCContent || {}
      dispatch(
        setXoPPDCContent({
          ...xoPPDCContent,
          apiError: {
            type: err.type,
            message: err.message,
            code: err.code,
          },
        }),
      )
      return null
      // Don't call handleFailedFetch - bypasses middleware completely
      // Note: Using 'apiError' instead of 'error' to avoid middleware interception
    }
  }
}

function extractInstrumentationData(recommendations) {
  let xt = ''
  let xe = ''
  let instrumentationData = ''

  if (!recommendations?.length) {
    return { xt, xe, instrumentationData }
  }

  for (const rec of recommendations) {
    const contentJson = rec.messages?.[0]?.content?.json
    if (contentJson?.xt && contentJson?.xe && contentJson?.instrumentationData) {
      xt = contentJson.xt
      xe = contentJson.xe
      instrumentationData = contentJson.instrumentationData
      break
    }
  }

  return { xt, xe, instrumentationData }
}

/**
 * Helper function to extract and trigger click URL from recommendations
 * This is for Analytics tracking - when user clicks CTA button
 * Uses image pixel to avoid CORS issues
 */
export function triggerClickUrl(recommendations) {
  try {
    if (!recommendations || recommendations.length === 0) {
      return
    }
    const clickUrl = recommendations[0]?.messages?.[0]?.trackingDetails?.clickUrl

    if (clickUrl) {
      const img = new Image()
      img.src = clickUrl
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[ucs] Error extracting click URL:', err.message)
  }
}
